import type { Language } from '../types';

export interface UITranslations {
  home: {
    title: string;
    subtitle: string;
    createRoomTitle: string;
    createRoomDescription: string;
    createRoom: string;
    joinRoomTitle: string;
    joinRoomDescription: string;
    joinRoom: string;
    whatIsTitle: string;
    description1: string;
    description2: string;
  };
  createRoom: {
    title: string;
    subtitle: string;
    roomName: string;
    roomNamePlaceholder: string;
    playerName: string;
    playerNamePlaceholder: string;
    email: string;
    emailPlaceholder: string;
    create: string;
    cancel: string;
  };
  joinRoom: {
    title: string;
    subtitle: string;
    roomCode: string;
    roomCodePlaceholder: string;
    playerName: string;
    playerNamePlaceholder: string;
    email: string;
    emailPlaceholder: string;
    join: string;
    cancel: string;
  };
  room: {
    title: string;
    boards: string;
    createBoard: string;
    boardName: string;
    boardNamePlaceholder: string;
    noBoards: string;
    addFirstBoard: string;
    deleteBoard: string;
    players: string;
    addActivity: string;
    activityTitle: string;
    activityTitlePlaceholder: string;
    activityDescription: string;
    activityDescriptionPlaceholder: string;
    assignee: string;
    assigneePlaceholder: string;
    saveActivity: string;
    cancel: string;
    deleteActivity: string;
    comments: string;
    addComment: string;
    commentPlaceholder: string;
    noActivities: string;
    moveNotAllowed: string;
    history: string;
    noHistory: string;
    taskType: string;
    taskTypePlaceholder: string;
    taskTypes: string;
    taskTypesPlaceholder: string;
    filterMyTasks: string;
    copyLink: string;
    linkCopied: string;
    saveSuccess: string;
    backToBoards: string;
    columns: string;
    defaultColumns: string;
  };
}

const uiTranslations: Record<Language, UITranslations> = {
  en: {
    home: {
      title: 'Kanban Board',
      subtitle: 'Visualize and manage your team workflow with collaborative kanban boards',
      createRoomTitle: 'Create a Room',
      createRoomDescription: 'Start a new kanban board session and invite your team',
      createRoom: 'Create Room',
      joinRoomTitle: 'Join a Room',
      joinRoomDescription: 'Join an existing session with a room code',
      joinRoom: 'Join Room',
      whatIsTitle: 'What is Kanban Board?',
      description1:
        'Kanban Board is a visual project management tool that helps teams organize and track tasks across customizable workflow stages. Activities move from left to right as work progresses.',
      description2:
        'With real-time collaboration, drag-and-drop task management, and comments, your team can stay aligned and deliver work efficiently — all in one shared board.',
    },
    createRoom: {
      title: 'Create a Room',
      subtitle: 'Set up a new kanban board session',
      roomName: 'Room Name',
      roomNamePlaceholder: 'Enter room name',
      playerName: 'Your Name',
      playerNamePlaceholder: 'Enter your name',
      email: 'Email',
      emailPlaceholder: 'Enter your email',
      create: 'Create Room',
      cancel: 'Cancel',
    },
    joinRoom: {
      title: 'Join a Room',
      subtitle: 'Join an existing kanban board session',
      roomCode: 'Room Code',
      roomCodePlaceholder: 'Enter room code',
      playerName: 'Your Name',
      playerNamePlaceholder: 'Enter your name',
      email: 'Email',
      emailPlaceholder: 'Enter your email',
      join: 'Join Room',
      cancel: 'Cancel',
    },
    room: {
      title: 'Kanban Board Room',
      boards: 'Boards',
      createBoard: 'New Board',
      boardName: 'Board Name',
      boardNamePlaceholder: 'Enter board name',
      noBoards: 'No boards yet',
      addFirstBoard: 'Create your first board to start organizing tasks',
      deleteBoard: 'Delete Board',
      players: 'Players',
      addActivity: 'Add Task',
      activityTitle: 'Task Title',
      activityTitlePlaceholder: 'Enter task title',
      activityDescription: 'Description',
      activityDescriptionPlaceholder: 'Enter task description (optional)',
      assignee: 'Assignee',
      assigneePlaceholder: 'Assign to someone (optional)',
      saveActivity: 'Save Task',
      cancel: 'Cancel',
      deleteActivity: 'Delete Task',
      comments: 'Comments',
      addComment: 'Add Comment',
      commentPlaceholder: 'Write a comment...',
      noActivities: 'No tasks in this column',
      moveNotAllowed: 'You can only move tasks to the next or previous column.',
      history: 'History',
      noHistory: 'No movement history yet.',
      taskType: 'Task Type',
      taskTypePlaceholder: 'Select a type',
      taskTypes: 'Task Types',
      taskTypesPlaceholder: 'Feature, Bug, Analise',
      filterMyTasks: 'My Tasks',
      copyLink: 'Copy Link',
      linkCopied: 'Link Copied!',
      saveSuccess: 'Saved successfully!',
      backToBoards: 'Back to Boards',
      columns: 'Columns',
      defaultColumns: 'To Do, In Progress, Done',
    },
  },
  pt: {
    home: {
      title: 'Kanban Board',
      subtitle: 'Visualize e gerencie o fluxo de trabalho da sua equipe com quadros kanban colaborativos',
      createRoomTitle: 'Criar uma Sala',
      createRoomDescription: 'Inicie uma nova sessão de kanban board e convide sua equipe',
      createRoom: 'Criar Sala',
      joinRoomTitle: 'Entrar em uma Sala',
      joinRoomDescription: 'Entre em uma sessão existente com um código de sala',
      joinRoom: 'Entrar na Sala',
      whatIsTitle: 'O que é Kanban Board?',
      description1:
        'Kanban Board é uma ferramenta visual de gerenciamento de projetos que ajuda equipes a organizar e acompanhar tarefas em etapas de fluxo de trabalho personalizáveis. As atividades se movem da esquerda para a direita conforme o trabalho avança.',
      description2:
        'Com colaboração em tempo real, gerenciamento de tarefas por arrastar e soltar e comentários, sua equipe pode se manter alinhada e entregar trabalho com eficiência — tudo em um quadro compartilhado.',
    },
    createRoom: {
      title: 'Criar uma Sala',
      subtitle: 'Configure uma nova sessão de kanban board',
      roomName: 'Nome da Sala',
      roomNamePlaceholder: 'Digite o nome da sala',
      playerName: 'Seu Nome',
      playerNamePlaceholder: 'Digite seu nome',
      email: 'Email',
      emailPlaceholder: 'Digite seu email',
      create: 'Criar Sala',
      cancel: 'Cancelar',
    },
    joinRoom: {
      title: 'Entrar em uma Sala',
      subtitle: 'Entre em uma sessão existente de kanban board',
      roomCode: 'Código da Sala',
      roomCodePlaceholder: 'Digite o código da sala',
      playerName: 'Seu Nome',
      playerNamePlaceholder: 'Digite seu nome',
      email: 'Email',
      emailPlaceholder: 'Digite seu email',
      join: 'Entrar na Sala',
      cancel: 'Cancelar',
    },
    room: {
      title: 'Sala de Kanban Board',
      boards: 'Quadros',
      createBoard: 'Novo Quadro',
      boardName: 'Nome do Quadro',
      boardNamePlaceholder: 'Digite o nome do quadro',
      noBoards: 'Nenhum quadro ainda',
      addFirstBoard: 'Crie seu primeiro quadro para começar a organizar as tarefas',
      deleteBoard: 'Excluir Quadro',
      players: 'Participantes',
      addActivity: 'Adicionar Tarefa',
      activityTitle: 'Título da Tarefa',
      activityTitlePlaceholder: 'Digite o título da tarefa',
      activityDescription: 'Descrição',
      activityDescriptionPlaceholder: 'Digite a descrição da tarefa (opcional)',
      assignee: 'Responsável',
      assigneePlaceholder: 'Atribuir a alguém (opcional)',
      saveActivity: 'Salvar Tarefa',
      cancel: 'Cancelar',
      deleteActivity: 'Excluir Tarefa',
      comments: 'Comentários',
      addComment: 'Adicionar Comentário',
      commentPlaceholder: 'Escreva um comentário...',
      noActivities: 'Nenhuma tarefa nesta coluna',
      moveNotAllowed: 'Voce so pode mover tarefas para a coluna anterior ou seguinte.',
      history: 'Historico',
      noHistory: 'Sem historico de movimentacao.',
      taskType: 'Tipo da Tarefa',
      taskTypePlaceholder: 'Selecione um tipo',
      taskTypes: 'Tipos de Tarefa',
      taskTypesPlaceholder: 'Feature, Bug, Analise',
      filterMyTasks: 'Minhas Tarefas',
      copyLink: 'Copiar Link',
      linkCopied: 'Link Copiado!',
      saveSuccess: 'Salvo com sucesso!',
      backToBoards: 'Voltar aos Quadros',
      columns: 'Colunas',
      defaultColumns: 'A Fazer, Em Andamento, Concluído',
    },
  },
  es: {
    home: {
      title: 'Kanban Board',
      subtitle: 'Visualiza y gestiona el flujo de trabajo de tu equipo con tableros kanban colaborativos',
      createRoomTitle: 'Crear una Sala',
      createRoomDescription: 'Inicia una nueva sesión de kanban board e invita a tu equipo',
      createRoom: 'Crear Sala',
      joinRoomTitle: 'Unirse a una Sala',
      joinRoomDescription: 'Únete a una sesión existente con un código de sala',
      joinRoom: 'Unirse a la Sala',
      whatIsTitle: '¿Qué es Kanban Board?',
      description1:
        'Kanban Board es una herramienta visual de gestión de proyectos que ayuda a los equipos a organizar y realizar un seguimiento de las tareas en etapas de flujo de trabajo personalizables. Las actividades se mueven de izquierda a derecha a medida que avanza el trabajo.',
      description2:
        'Con colaboración en tiempo real, gestión de tareas mediante arrastrar y soltar y comentarios, tu equipo puede mantenerse alineado y entregar trabajo de manera eficiente, todo en un tablero compartido.',
    },
    createRoom: {
      title: 'Crear una Sala',
      subtitle: 'Configura una nueva sesión de kanban board',
      roomName: 'Nombre de la Sala',
      roomNamePlaceholder: 'Ingresa el nombre de la sala',
      playerName: 'Tu Nombre',
      playerNamePlaceholder: 'Ingresa tu nombre',
      email: 'Email',
      emailPlaceholder: 'Ingresa tu email',
      create: 'Crear Sala',
      cancel: 'Cancelar',
    },
    joinRoom: {
      title: 'Unirse a una Sala',
      subtitle: 'Únete a una sesión existente de kanban board',
      roomCode: 'Código de Sala',
      roomCodePlaceholder: 'Ingresa el código de sala',
      playerName: 'Tu Nombre',
      playerNamePlaceholder: 'Ingresa tu nombre',
      email: 'Email',
      emailPlaceholder: 'Ingresa tu email',
      join: 'Unirse a la Sala',
      cancel: 'Cancelar',
    },
    room: {
      title: 'Sala de Kanban Board',
      boards: 'Tableros',
      createBoard: 'Nuevo Tablero',
      boardName: 'Nombre del Tablero',
      boardNamePlaceholder: 'Ingresa el nombre del tablero',
      noBoards: 'No hay tableros aún',
      addFirstBoard: 'Crea tu primer tablero para comenzar a organizar las tareas',
      deleteBoard: 'Eliminar Tablero',
      players: 'Participantes',
      addActivity: 'Agregar Tarea',
      activityTitle: 'Título de la Tarea',
      activityTitlePlaceholder: 'Ingresa el título de la tarea',
      activityDescription: 'Descripción',
      activityDescriptionPlaceholder: 'Ingresa la descripción de la tarea (opcional)',
      assignee: 'Responsable',
      assigneePlaceholder: 'Asignar a alguien (opcional)',
      saveActivity: 'Guardar Tarea',
      cancel: 'Cancelar',
      deleteActivity: 'Eliminar Tarea',
      comments: 'Comentarios',
      addComment: 'Agregar Comentario',
      commentPlaceholder: 'Escribe un comentario...',
      noActivities: 'No hay tareas en esta columna',
      moveNotAllowed: 'Solo puedes mover tareas a la columna anterior o siguiente.',
      history: 'Historial',
      noHistory: 'No hay historial de movimientos.',
      taskType: 'Tipo de Tarea',
      taskTypePlaceholder: 'Selecciona un tipo',
      taskTypes: 'Tipos de Tarea',
      taskTypesPlaceholder: 'Feature, Bug, Analise',
      filterMyTasks: 'Mis Tareas',
      copyLink: 'Copiar Enlace',
      linkCopied: '¡Enlace Copiado!',
      saveSuccess: 'Guardado con exito!',
      backToBoards: 'Volver a Tableros',
      columns: 'Columnas',
      defaultColumns: 'Por Hacer, En Progreso, Hecho',
    },
  },
  fr: {
    home: {
      title: 'Kanban Board',
      subtitle:
        'Visualisez et gérez le flux de travail de votre équipe avec des tableaux kanban collaboratifs',
      createRoomTitle: 'Créer une Salle',
      createRoomDescription: 'Démarrez une nouvelle session de kanban board et invitez votre équipe',
      createRoom: 'Créer une Salle',
      joinRoomTitle: 'Rejoindre une Salle',
      joinRoomDescription: 'Rejoignez une session existante avec un code de salle',
      joinRoom: 'Rejoindre la Salle',
      whatIsTitle: "Qu'est-ce que Kanban Board ?",
      description1:
        "Kanban Board est un outil visuel de gestion de projet qui aide les équipes à organiser et suivre les tâches dans des étapes de flux de travail personnalisables. Les activités se déplacent de gauche à droite au fur et à mesure de l'avancement du travail.",
      description2:
        "Avec la collaboration en temps réel, la gestion des tâches par glisser-déposer et les commentaires, votre équipe peut rester alignée et livrer le travail efficacement, le tout dans un tableau partagé.",
    },
    createRoom: {
      title: 'Créer une Salle',
      subtitle: 'Configurez une nouvelle session de kanban board',
      roomName: 'Nom de la Salle',
      roomNamePlaceholder: 'Entrez le nom de la salle',
      playerName: 'Votre Nom',
      playerNamePlaceholder: 'Entrez votre nom',
      email: 'Email',
      emailPlaceholder: 'Entrez votre email',
      create: 'Créer la Salle',
      cancel: 'Annuler',
    },
    joinRoom: {
      title: 'Rejoindre une Salle',
      subtitle: 'Rejoignez une session existante de kanban board',
      roomCode: 'Code de Salle',
      roomCodePlaceholder: 'Entrez le code de salle',
      playerName: 'Votre Nom',
      playerNamePlaceholder: 'Entrez votre nom',
      email: 'Email',
      emailPlaceholder: 'Entrez votre email',
      join: 'Rejoindre la Salle',
      cancel: 'Annuler',
    },
    room: {
      title: 'Salle de Kanban Board',
      boards: 'Tableaux',
      createBoard: 'Nouveau Tableau',
      boardName: 'Nom du Tableau',
      boardNamePlaceholder: 'Entrez le nom du tableau',
      noBoards: 'Aucun tableau pour le moment',
      addFirstBoard: 'Créez votre premier tableau pour commencer à organiser les tâches',
      deleteBoard: 'Supprimer le Tableau',
      players: 'Participants',
      addActivity: 'Ajouter une Tâche',
      activityTitle: 'Titre de la Tâche',
      activityTitlePlaceholder: 'Entrez le titre de la tâche',
      activityDescription: 'Description',
      activityDescriptionPlaceholder: 'Entrez la description de la tâche (optionnel)',
      assignee: 'Responsable',
      assigneePlaceholder: "Assigner à quelqu'un (optionnel)",
      saveActivity: 'Enregistrer la Tâche',
      cancel: 'Annuler',
      deleteActivity: 'Supprimer la Tâche',
      comments: 'Commentaires',
      addComment: 'Ajouter un Commentaire',
      commentPlaceholder: 'Écrire un commentaire...',
      noActivities: 'Aucune tâche dans cette colonne',
      moveNotAllowed: 'Vous ne pouvez deplacer une tache que vers la colonne precedente ou suivante.',
      history: 'Historique',
      noHistory: 'Aucun historique de deplacement.',
      taskType: 'Type de Tache',
      taskTypePlaceholder: 'Selectionnez un type',
      taskTypes: 'Types de Tache',
      taskTypesPlaceholder: 'Feature, Bug, Analise',
      filterMyTasks: 'Mes Tâches',
      copyLink: 'Copier le Lien',
      linkCopied: 'Lien Copié !',
      saveSuccess: 'Enregistre avec succes !',
      backToBoards: 'Retour aux Tableaux',
      columns: 'Colonnes',
      defaultColumns: 'À Faire, En Cours, Terminé',
    },
  },
  de: {
    home: {
      title: 'Kanban Board',
      subtitle:
        'Visualisieren und verwalten Sie den Arbeitsfluss Ihres Teams mit kollaborativen Kanban-Boards',
      createRoomTitle: 'Einen Raum Erstellen',
      createRoomDescription:
        'Starten Sie eine neue Kanban-Board-Sitzung und laden Sie Ihr Team ein',
      createRoom: 'Raum Erstellen',
      joinRoomTitle: 'Einem Raum Beitreten',
      joinRoomDescription: 'Treten Sie einer bestehenden Sitzung mit einem Raumcode bei',
      joinRoom: 'Raum Beitreten',
      whatIsTitle: 'Was ist Kanban Board?',
      description1:
        'Kanban Board ist ein visuelles Projektmanagement-Tool, das Teams hilft, Aufgaben in anpassbaren Workflow-Phasen zu organisieren und zu verfolgen. Aktivitäten bewegen sich von links nach rechts, während die Arbeit voranschreitet.',
      description2:
        'Mit Echtzeit-Zusammenarbeit, Drag-and-Drop-Aufgabenverwaltung und Kommentaren kann Ihr Team ausgerichtet bleiben und Arbeit effizient liefern – alles in einem gemeinsamen Board.',
    },
    createRoom: {
      title: 'Einen Raum Erstellen',
      subtitle: 'Richten Sie eine neue Kanban-Board-Sitzung ein',
      roomName: 'Raumname',
      roomNamePlaceholder: 'Geben Sie den Raumnamen ein',
      playerName: 'Ihr Name',
      playerNamePlaceholder: 'Geben Sie Ihren Namen ein',
      email: 'Email',
      emailPlaceholder: 'Geben Sie Ihre Email ein',
      create: 'Raum Erstellen',
      cancel: 'Abbrechen',
    },
    joinRoom: {
      title: 'Einem Raum Beitreten',
      subtitle: 'Treten Sie einer bestehenden Kanban-Board-Sitzung bei',
      roomCode: 'Raumcode',
      roomCodePlaceholder: 'Geben Sie den Raumcode ein',
      playerName: 'Ihr Name',
      playerNamePlaceholder: 'Geben Sie Ihren Namen ein',
      email: 'Email',
      emailPlaceholder: 'Geben Sie Ihre Email ein',
      join: 'Raum Beitreten',
      cancel: 'Abbrechen',
    },
    room: {
      title: 'Kanban-Board-Raum',
      boards: 'Boards',
      createBoard: 'Neues Board',
      boardName: 'Board-Name',
      boardNamePlaceholder: 'Geben Sie den Board-Namen ein',
      noBoards: 'Noch keine Boards',
      addFirstBoard: 'Erstellen Sie Ihr erstes Board, um mit der Aufgabenorganisation zu beginnen',
      deleteBoard: 'Board Löschen',
      players: 'Teilnehmer',
      addActivity: 'Aufgabe Hinzufügen',
      activityTitle: 'Aufgabentitel',
      activityTitlePlaceholder: 'Geben Sie den Aufgabentitel ein',
      activityDescription: 'Beschreibung',
      activityDescriptionPlaceholder: 'Geben Sie die Aufgabenbeschreibung ein (optional)',
      assignee: 'Verantwortlicher',
      assigneePlaceholder: 'Jemanden zuweisen (optional)',
      saveActivity: 'Aufgabe Speichern',
      cancel: 'Abbrechen',
      deleteActivity: 'Aufgabe Löschen',
      comments: 'Kommentare',
      addComment: 'Kommentar Hinzufügen',
      commentPlaceholder: 'Einen Kommentar schreiben...',
      noActivities: 'Keine Aufgaben in dieser Spalte',
      moveNotAllowed: 'Du kannst Aufgaben nur in die vorherige oder nachste Spalte verschieben.',
      history: 'Verlauf',
      noHistory: 'Kein Bewegungsverlauf vorhanden.',
      taskType: 'Aufgabentyp',
      taskTypePlaceholder: 'Typ auswahlen',
      taskTypes: 'Aufgabentypen',
      taskTypesPlaceholder: 'Feature, Bug, Analise',
      filterMyTasks: 'Meine Aufgaben',
      copyLink: 'Link Kopieren',
      linkCopied: 'Link Kopiert!',
      saveSuccess: 'Erfolgreich gespeichert!',
      backToBoards: 'Zurück zu Boards',
      columns: 'Spalten',
      defaultColumns: 'Zu Erledigen, In Bearbeitung, Erledigt',
    },
  },
};

export const getUITranslation = (language: Language): UITranslations => {
  return uiTranslations[language];
};
